import { dialogActionsClasses } from '@mui/material/DialogActions'
import { dialogContentClasses } from '@mui/material/DialogContent'
import { dialogTitleClasses } from '@mui/material/DialogTitle'
import { Theme } from '@mui/material/styles'

import { layout } from '../tokens.generated'

// ----------------------------------------------------------------------
// A modal is the one surface that takes the modal shadow (e3), always with a
// scrim. Title, content and actions each carry the s6 (24) inset, and the
// action buttons are laid out with `gap`, never with sibling margins, so a
// conditionally rendered button does not leave a hole and a wrapped button
// does not lose its spacing.
//
// **One inset between the last field and the buttons, not two.** The actions
// row is the foot of whatever is above it, and that block already ends on its
// own 24: a `DialogContent`, or the padded box a form dialog draws its fields
// in. With a top inset of its own as well, nine dialogs put 48px of nothing
// between the last field and Cancel, where the two dozen that end their stack
// on an `ActionRow` put 24. Straight under a title there is no block above it,
// so there it takes the top inset back, the way MUI takes it off a
// `DialogContent` in the same place.
//
// **Under a part that scrolls, the foot holds that inset itself.** A
// `DialogContent` scrolls and the actions row under it stays put, so the
// content's own bottom inset is at the end of what scrolls, out of sight
// until the reader gets there. Until then the content ran on to the buttons'
// top edge and was cut off there, and the buttons' edge was the foot's
// (reported 2026-10-19 on the booking calendar's Failed booking dialog). So
// the inset moves from the content to the foot: the content ends on nothing
// and the foot stands as tall above its buttons as below them. Scrolled to
// the end it is still one inset between the last field and Cancel.
// ----------------------------------------------------------------------

/**
 * MUI's own dialog paper margin. The `scrollBody` width fix below has to know
 * it, because that is the width at which a small dialog stops fitting beside
 * its margins on a phone.
 */
const DIALOG_PAPER_MARGIN = 32

/**
 * Marks the close button `components/Dialog` draws, so the paper can place it.
 * An attribute rather than a class, because a class on an `IconButton` is a
 * styling hook any screen could also pass, and this one is the dialog's.
 */
export const DIALOG_CLOSE_ATTRIBUTE = 'data-dialog-close'

/**
 * Where the close mark stands, in spacing units from the paper's top and
 * right edges. A 36px button there puts its glyph's centre level with the
 * first line of a title set at the 24 inset, and the glyph's edge on that
 * inset.
 */
const CLOSE_OFFSET = 2

/**
 * The right inset of a dialog's first line: the close mark's offset, its box
 * and a control gap after it. A title takes it, and so does a row drawn where
 * a title would be, so a long one wraps before it reaches the mark instead of
 * running under it.
 */
export const dialogTitleClearance = (theme: Theme) =>
  `${
    Number.parseFloat(theme.spacing(CLOSE_OFFSET)) +
    layout.iconButtonSize +
    layout.controlGap
  }px`

export default function Dialog(theme: Theme) {
  const titleClearance = dialogTitleClearance(theme)

  return {
    MuiDialog: {
      styleOverrides: {
        paper: {
          'boxShadow': theme.customShadows.dialog,
          '&.MuiPaper-rounded': {
            borderRadius: theme.radii.surface,
          },
          '&.MuiDialog-paperFullScreen': {
            borderRadius: 0,
          },
          [theme.breakpoints.down('sm')]: {
            margin: theme.spacing(2),
          },
          [theme.breakpoints.down(
            theme.breakpoints.values.sm + 2 * DIALOG_PAPER_MARGIN,
          )]: {
            '&.MuiDialog-paperWidthSm.MuiDialog-paperScrollBody': {
              maxWidth: '100%',
            },
          },
          // The way out, in the corner of every dialog. It is the paper's
          // last child and sits above whatever the content draws there.
          [`& > [${DIALOG_CLOSE_ATTRIBUTE}]`]: {
            position: 'absolute',
            top: theme.spacing(CLOSE_OFFSET),
            right: theme.spacing(CLOSE_OFFSET),
            zIndex: 1,
          },
        },
        paperFullWidth: {
          width: '100%',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: theme.spacing(3, 3, 0),
          paddingRight: titleClearance,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          borderTop: 0,
          borderBottom: 0,
          padding: theme.spacing(3),
          // **A dialog scrolls in one place.** On a short window the content
          // scrolls and the paper around it must not. An absolutely positioned
          // box is clipped by a scroller only when the scroller, or something
          // inside it, is its containing block, and MUI leaves this one
          // static. So a visually hidden line inside it (a `BlockedHint`
          // reason, `visuallyHidden` text) took the paper as its containing
          // block, escaped the clip, and stretched the paper's own scroll
          // area: Adjust the price drew two scrollbars side by side on a
          // Windows laptop (2026-10-08), the paper's for 53px of nothing.
          // Positioned, the content holds whatever it draws.
          position: 'relative',
          // The foot under it holds the inset between them (see the top).
          [`&:has(+ .${dialogActionsClasses.root})`]: {
            paddingBottom: 0,
          },
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          'padding': theme.spacing(0, 3, 3),
          [`.${dialogTitleClasses.root} + &`]: {
            paddingTop: theme.spacing(3),
          },
          [`.${dialogContentClasses.root} + &`]: {
            paddingTop: theme.spacing(3),
          },
          'gap': theme.spacing(1),
          // MUI spaces the buttons with a margin on every sibling after the
          // first. `gap` owns that now, so the margin is zeroed rather than
          // doubled.
          '& > :not(style) ~ :not(style)': {
            marginLeft: 0,
          },
        },
      },
    },
  }
}
